import React from 'react';
import { Pagination } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { getReportPagedData, setReportPagination } from '@store/DynamicSlice/reportTableSlice';

interface ReportPaginationProps {
  reportKey: string;
  className?: string;
  maxVisiblePages?: number;
  showItemsPerPage?: boolean;
  showGoToPage?: boolean;
}

const getDir = () => {
  const dir =
    (typeof document !== 'undefined' &&
      (document.documentElement.getAttribute('dir') || document.body.getAttribute('dir'))) ||
    'ltr';
  return String(dir).toLowerCase();
};

export const ReportPagination: React.FC<ReportPaginationProps> = ({
  reportKey,
  className,
  maxVisiblePages = 5,
  showItemsPerPage = true,
  showGoToPage = false
}) => {
  const dispatch = useAppDispatch();

  const reportState = useAppSelector((state: any) => state.reportTable?.[reportKey]);
  const meta = reportState?.meta;
  const req = reportState?.request ?? { pageNumber: 1, pageSize: 10 };

  const pageNumber = Number(meta?.pageNumber ?? req.pageNumber ?? 1); // ✅ 1-based
  const pageSize = Number(meta?.pageSize ?? req.pageSize ?? 10);
  const totalRows = Number(meta?.totalRows ?? 0);
  const totalPages = Math.max(1, Number(meta?.totalPages ?? 1));

  if (totalPages <= 1) return null;

  const dir = getDir();
  const isRTL = dir === 'rtl';

  const ICON = {
    prev: isRTL ? 'fa-chevron-right' : 'fa-chevron-left',
    next: isRTL ? 'fa-chevron-left' : 'fa-chevron-right',
    first: isRTL ? 'fa-angles-right' : 'fa-angles-left',
    last: isRTL ? 'fa-angles-left' : 'fa-angles-right'
  };

  const startItem = totalRows === 0 ? 0 : Math.min((pageNumber - 1) * pageSize + 1, totalRows);
  const endItem = totalRows === 0 ? 0 : Math.min(pageNumber * pageSize, totalRows);

  const getPageNumbers = () => {
    const pages: number[] = [];

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }

    const startPage = Math.max(1, Math.min(pageNumber - Math.floor(maxVisiblePages / 2), totalPages - maxVisiblePages + 1));
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    for (let i = startPage; i <= endPage; i++) pages.push(i);
    return pages;
  };

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    dispatch(setReportPagination({ reportKey, pageIndex: newPage, pageSize }));
    dispatch(getReportPagedData({ reportKey }));
  };

  const pageNumbers = getPageNumbers();

  return (
    <div className={`w-100 ${className ?? ''}`}>
      <div className="row align-items-center g-3">
        {/* Info */}
        <div className="col-12 col-md-auto order-1 order-md-1">
          <div className="text-muted small d-flex align-items-center">
            <span>
              عرض <span className="fw-bold text-theme">{startItem}</span> إلى{' '}
              <span className="fw-bold text-theme">{endItem}</span> من{' '}
              <span className="fw-bold">{totalRows}</span> عنصر
            </span>
          </div>
        </div>

        {/* Pagination */}
        <div className="col-12 col-md order-3 order-md-2">
          <div className="d-flex flex-wrap justify-content-center justify-content-md-end align-items-center gap-2">
            {showItemsPerPage && (
              <div className="d-none d-md-flex align-items-center">
                <span className="me-2 small text-muted">عدد:</span>
                <select
                  className="form-select form-select-sm w-auto"
                  value={pageSize}
                  onChange={e => {
                    const newPageSize = Number(e.target.value);
                    dispatch(setReportPagination({ reportKey, pageIndex: 0, pageSize: newPageSize }));
                    dispatch(getReportPagedData({ reportKey }));
                  }}
                >
                  {[5, 10, 25, 50, 100, 200].map(n => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div dir={isRTL ? 'rtl' : 'ltr'}>
              {/* Mobile */}
              <Pagination className="mb-0 d-flex d-md-none align-items-center flex-wrap">
                <Pagination.Prev onClick={() => handlePageChange(Math.max(1, pageNumber - 1))} disabled={pageNumber === 1}>
                  <i className={`fas ${ICON.prev}`}></i>
                </Pagination.Prev>

                <Pagination.Item active className="px-2" disabled>
                  <span className="fw-bold">{pageNumber}</span>
                  <span className="mx-1 text-muted">/</span>
                  <span>{totalPages}</span>
                </Pagination.Item>

                <Pagination.Next
                  onClick={() => handlePageChange(Math.min(totalPages, pageNumber + 1))}
                  disabled={pageNumber === totalPages}
                >
                  <i className={`fas ${ICON.next}`}></i>
                </Pagination.Next>
              </Pagination>

              {/* Desktop */}
              <Pagination className="mb-0 d-none d-md-flex align-items-center flex-wrap">
                <Pagination.First onClick={() => handlePageChange(1)} disabled={pageNumber === 1}>
                  <i className={`fas ${ICON.first}`}></i>
                </Pagination.First>

                <Pagination.Prev onClick={() => handlePageChange(Math.max(1, pageNumber - 1))} disabled={pageNumber === 1}>
                  <i className={`fas ${ICON.prev}`}></i>
                </Pagination.Prev>

                {pageNumbers[0] > 1 && (
                  <>
                    <Pagination.Item onClick={() => handlePageChange(1)}>1</Pagination.Item>
                    {pageNumbers[0] > 2 && <Pagination.Ellipsis disabled />}
                  </>
                )}

                {pageNumbers.map(p => (
                  <Pagination.Item key={p} active={p === pageNumber} onClick={() => handlePageChange(p)}>
                    {p}
                  </Pagination.Item>
                ))}

                {pageNumbers[pageNumbers.length - 1] < totalPages && (
                  <>
                    {pageNumbers[pageNumbers.length - 1] < totalPages - 1 && <Pagination.Ellipsis disabled />}
                    <Pagination.Item onClick={() => handlePageChange(totalPages)}>{totalPages}</Pagination.Item>
                  </>
                )}

                <Pagination.Next
                  onClick={() => handlePageChange(Math.min(totalPages, pageNumber + 1))}
                  disabled={pageNumber === totalPages}
                >
                  <i className={`fas ${ICON.next}`}></i>
                </Pagination.Next>

                <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={pageNumber === totalPages}>
                  <i className={`fas ${ICON.last}`}></i>
                </Pagination.Last>
              </Pagination>
            </div>

            {showGoToPage && (
              <div className="d-none d-lg-flex align-items-center">
                <span className="me-2 small text-muted">اذهب:</span>
                <div className="input-group input-group-sm w-auto">
                  <input
                    type="number"
                    className="form-control text-center"
                    min={1}
                    max={totalPages}
                    value={pageNumber}
                    onChange={e => {
                      const v = parseInt(e.target.value);
                      if (!Number.isNaN(v) && v >= 1 && v <= totalPages) handlePageChange(v);
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {showItemsPerPage && (
          <div className="col-12 order-2 order-md-3 d-flex d-md-none justify-content-center">
            <div className="d-flex align-items-center">
              <span className="me-2 small text-muted">عدد العناصر:</span>
              <select
                className="form-select form-select-sm w-auto"
                value={pageSize}
                onChange={e => {
                  const newPageSize = Number(e.target.value);
                  dispatch(setReportPagination({ reportKey, pageIndex: 0, pageSize: newPageSize }));
                  dispatch(getReportPagedData({ reportKey }));
                }}
              >
                {[5, 10, 25, 50].map(n => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportPagination;
